// Guided setup, step 2: Benefits. Reuses the per-employee eligibility editor (hr.benefit.manage). Eligibility is a REVIEW outcome only: nothing here
// enrolls anyone or starts coverage, and no rule is guessed (no 401(k) calendar, no manager-title classification).
//
// Cycle 12: each applicable plan needs an explicit decision - Eligible (with the date the employee will see) or Not eligible. A plan's waiting-period default only
// SUGGESTS the date; HR confirms or overrides it. The acknowledgment lives in the shell's footer next to Save & continue ("Benefits reviewed", or "No benefits to
// review" when no plan applies) and is saved by that one action; it is enabled only when every applicable plan is decided. There is no separate "Mark reviewed" button.
import { useEffect, useRef, useState } from 'react';
import { AdminBenefitsEligibilitySection, type FlushResult } from '../workspace/AdminBenefitsEligibilitySection';
import { StepCard, UnavailableNotice } from './stepUi';
import { benefitsAck, type ReviewCounts } from './benefitsModel';
import type { StepContext } from './setupTypes';

const HELP_BODY = (
  <>
    <p>Choose <strong>Eligible</strong> or <strong>Not eligible</strong> for each benefit that applies to this person. An Eligible choice needs the date the employee is shown as “Eligible starting”.</p>
    <p className="mt-2">When a benefit has a waiting period, the suggested date is the hire date plus that many calendar days. It is only a suggestion: you confirm or change it.</p>
    <p className="mt-2">This records a review only. It does not enroll anyone, start coverage or change payroll.</p>
  </>
);

export function BenefitsStep({ ctx }: { ctx: StepContext }) {
  const b = ctx.view.summary.benefits;
  const savedReviewed = !!ctx.view.progress?.decisions.benefits?.reviewed;
  const [counts, setCounts] = useState<ReviewCounts | null>(null);
  const [ackChecked, setAckChecked] = useState(savedReviewed);
  const ackRef = useRef(ackChecked);
  ackRef.current = ackChecked;
  const savedRef = useRef(savedReviewed);
  savedRef.current = savedReviewed;
  const flushRef = useRef<(() => Promise<FlushResult>) | null>(null);
  const countsRef = useRef<ReviewCounts | null>(null);
  countsRef.current = counts;
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const available = b.state !== 'unavailable';

  // Save & continue: save every pending valid decision (per-plan audited writes), then record the acknowledgment with the same action.
  useEffect(() => {
    if (!available) { ctx.bindSaver(null); ctx.setDirty(false); return; }
    ctx.bindSaver(async () => {
      if (flushRef.current) {
        const r = await flushRef.current();
        if (!r.ok) return r;
      }
      const c = countsRef.current;
      const ack = c ? benefitsAck(c) : null;
      if (ackRef.current && ack && !ack.disabled) return { ok: true, decisions: { benefits: { reviewed: true } } };
      if (!ackRef.current && savedRef.current) return { ok: true, decisions: { benefits: null } }; // deliberate undo of the acknowledgment; no eligibility record is touched
      return { ok: true };
    });
    return () => ctx.bindSaver(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  // The footer acknowledgment follows what HR has decided on screen.
  const ack = counts ? benefitsAck(counts) : null;
  const ackDisabled = !ack || ack.disabled;
  const ackLabel = ack?.label ?? 'Benefits reviewed';
  const ackHint = !counts ? 'Benefits are still loading.' : (ack?.hint ?? null);
  useEffect(() => {
    if (!available) { ctxRef.current.bindFooter({ help: { title: 'How benefits review works', body: HELP_BODY } }); return; }
    ctxRef.current.bindFooter({
      ack: { id: 'benefits-reviewed', label: ackLabel, checked: ackChecked && !ackDisabled, disabled: ackDisabled, hint: ackHint ?? undefined, onChange: setAckChecked },
      help: { title: 'How benefits review works', body: HELP_BODY },
    });
  }, [available, ackLabel, ackChecked, ackDisabled, ackHint]);
  useEffect(() => () => ctxRef.current.bindFooter(null), []);

  if (b.state === 'unavailable') {
    return (
      <StepCard title="Benefits" subtitle="Review which benefits this person is eligible for." testId="step-benefits">
        <UnavailableNotice title="Benefits" reason={b.reason} capability={b.capability} />
      </StepCard>
    );
  }
  const hireDate = b.hireDate ?? ctx.view.summary.details.hireDate ?? null;
  return (
    <StepCard title="Benefits" subtitle="Review which benefits this person is eligible for. This records a review only: it does not enroll anyone or start coverage." testId="step-benefits">
      <AdminBenefitsEligibilitySection
        employeeId={ctx.employeeId}
        employeeName={ctx.person.name}
        hireDate={hireDate}
        setupMode
        onCounts={setCounts}
        onDirtyChange={ctx.setDirty}
        bindFlush={(f) => { flushRef.current = f; }}
        onSaved={() => { void ctxRef.current.reload(); }}
      />
    </StepCard>
  );
}
